import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Select,
  useColorModeValue,
  useToast,
  Spinner,
  Center,
  useBreakpointValue,
  SimpleGrid,
  Stack
} from '@chakra-ui/react';
import {
  FiArrowLeft,
  FiCalendar,
  FiRefreshCw
} from 'react-icons/fi';
import IconWrapper from '../components/IconWrapper';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DashboardMetrics from '../components/DashboardMetrics';
import ContentPerformance from '../components/ContentPerformance';
import TopVideos from '../components/TopVideos';
import AnalyticsService from '../services/analyticsService';
import { getContentById } from '../services/contentService';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../lib/firebase';

interface Metrics {
  totalViews: number;
  totalSubscribers: number;
  totalLikes: number;
  totalShares: number;
  totalDonations: number;
  totalRevenue: number;
  totalWatchTime: number;
  viewsChange: number;
  subscribersChange: number;
  likesChange: number;
  revenueChange: number;
}

interface TopVideo {
  id: string;
  title: string;
  thumbnail: string;
  views: number;
  likes: number;
  duration: string;
  uploadDate: Date;
  engagementRate: number;
}

interface Performance {
  id: string;
  title: string;
  thumbnail: string;
  duration: string;
  views: number;
  likes: number;
  shares: number;
  donations: number;
  uploadDate: Date;
  category?: string;
}

const CreatorDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false });
  
  const [metrics, setMetrics] = useState<Metrics>({
    totalViews: 0,
    totalSubscribers: 0,
    totalLikes: 0,
    totalShares: 0,
    totalDonations: 0,
    totalRevenue: 0,
    totalWatchTime: 0,
    viewsChange: 0,
    subscribersChange: 0,
    likesChange: 0,
    revenueChange: 0
  });
  
  const [recentContent, setRecentContent] = useState<Performance[]>([]);
  const [filteredContent, setFilteredContent] = useState<Performance[]>([]);
  const [topVideos, setTopVideos] = useState<TopVideo[]>([]);
  const [timeRange, setTimeRange] = useState(28);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(true);

  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.600', 'gray.300');

  const loadDashboardData = async () => {
    if (!user?.uid) return;
    
    setIsLoading(true);
    try {
      // Get all user content, not just top performing (include all statuses for "Your Videos")
      const contentQuery = query(
        collection(db, 'content'),
        where('uploaderId', '==', user.uid),
        orderBy('createdAt', 'desc')
      );
      
      const contentSnapshot = await getDocs(contentQuery);
      const allUserContent = contentSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      }));
      
      // Get analytics for metrics with error handling for mobile
      let creatorAnalytics;
      try {
        creatorAnalytics = await AnalyticsService.getCreatorDashboardAnalytics(user.uid);
      } catch (error) {
        console.error('Error loading analytics, using defaults:', error);
        // Use default values if analytics fail to load
        creatorAnalytics = {
          totalViews: 0,
          totalRevenue: 0,
          totalSubscribers: 0,
          totalContent: allUserContent.length,
          averageRating: 0,
          topPerformingContent: [],
          recentActivity: []
        };
      }
      
      // Transform the data to match the expected interfaces
      const metricsData: Metrics = {
        totalViews: creatorAnalytics.totalViews,
        totalSubscribers: creatorAnalytics.totalSubscribers,
        totalLikes: 0, // Will be calculated from engagement analytics
        totalShares: 0, // Will be calculated from engagement analytics
        totalDonations: 0, // Will be calculated from revenue analytics
        totalRevenue: creatorAnalytics.totalRevenue,
        totalWatchTime: 0, // Will be calculated from view analytics
        viewsChange: 0, // Will be calculated from historical data
        subscribersChange: 0, // Will be calculated from historical data
        likesChange: 0, // Will be calculated from historical data
        revenueChange: 0 // Will be calculated from historical data
      };
      
      // Helper function to format duration correctly
      const formatDuration = (duration: number | null | undefined, category: string): string => {
        if (!duration && duration !== 0) return '0:00';
        
        // Duration is stored in minutes for most content, but shorts might be in seconds
        if (category === 'short') {
          // For shorts, duration might be in seconds or minutes
          if (duration < 60) {
            // If less than 60, assume it's in minutes, convert to seconds for display
            const seconds = Math.round(duration * 60);
            const mins = Math.floor(seconds / 60);
            const secs = seconds % 60;
            return `${mins}:${secs.toString().padStart(2, '0')}`;
          } else {
            // If 60 or more, it might already be in seconds
            const mins = Math.floor(duration / 60);
            const secs = Math.round(duration % 60);
            return `${mins}:${secs.toString().padStart(2, '0')}`;
          }
        } else {
          // For other content, duration is in minutes
          const hours = Math.floor(duration / 60);
          const minutes = Math.round(duration % 60);
          if (hours > 0) {
            return `${hours}h ${minutes}m`;
          }
          return `${minutes}m`;
        }
      };

      // Transform all user content to Performance format
      const recentContentData: Performance[] = allUserContent.map((content: any) => ({
        id: content.id,
        title: content.title || 'Untitled',
        thumbnail: content.thumbnail || content.poster || '',
        duration: formatDuration(content.duration, content.category || ''),
        views: content.views || 0,
        likes: content.likes || 0,
        shares: content.shares || 0,
        donations: 0, // Will be calculated from revenue analytics
        uploadDate: content.createdAt || new Date(),
        category: content.category
      }));
      
      // Sort by views for top videos (but show all content in recent)
      const topVideosData: TopVideo[] = recentContentData
        .sort((a, b) => b.views - a.views)
        .slice(0, 10)
        .map(content => ({
          id: content.id,
          title: content.title,
          thumbnail: content.thumbnail,
          views: content.views,
          likes: content.likes,
          duration: content.duration,
          uploadDate: content.uploadDate,
          engagementRate: content.views > 0 ? (content.likes / content.views) * 100 : 0
        }));

      setMetrics(metricsData);
      setRecentContent(recentContentData);
      setTopVideos(topVideosData);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast({
        title: 'Error loading dashboard',
        description: 'Failed to load dashboard data. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, [user?.uid, timeRange]);

  // Apply category filter whenever filter or content changes
  useEffect(() => {
    if (categoryFilter === 'all') {
      setFilteredContent(recentContent);
    } else {
      // Map filter values to actual category values
      const categoryMap: Record<string, string> = {
        'videos': 'movie',
        'short videos': 'short',
        'books': 'book',
        'series': 'series',
        'comics': 'comic',
        'movies': 'movie'
      };
      const mappedCategory = categoryMap[categoryFilter.toLowerCase()] || categoryFilter;
      setFilteredContent(recentContent.filter(item => item.category === mappedCategory));
    }
  }, [categoryFilter, recentContent]);

  // Set up real-time listeners for content stats updates
  useEffect(() => {
    if (!user?.uid || recentContent.length === 0) return;

    const { onSnapshot, doc } = require('firebase/firestore');
    const unsubscribeFunctions: (() => void)[] = [];

    // Listen to each content item for real-time updates
    recentContent.forEach((contentItem) => {
      const contentRef = doc(db, 'content', contentItem.id);
      
      const unsubscribe = onSnapshot(contentRef, (snapshot: any) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          setRecentContent(prev => prev.map(item => 
            item.id === contentItem.id 
              ? {
                  ...item,
                  views: data.views || 0,
                  likes: data.likes || 0,
                  shares: data.shares || 0
                }
              : item
          ));
        }
      }, (error: any) => {
        console.error('Error listening to content updates:', error);
      });

      unsubscribeFunctions.push(unsubscribe);
    });

    return () => {
      unsubscribeFunctions.forEach(unsub => unsub());
    };
  }, [user?.uid, recentContent.map(c => c.id).join(',')]);

  const handleTimeRangeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setTimeRange(parseInt(event.target.value));
  };

  const handleRefresh = () => {
    loadDashboardData();
    toast({
      title: 'Dashboard refreshed',
      status: 'success',
      duration: 2000,
      isClosable: true
    });
  };

  const handleViewDetails = (contentId: string) => {
    navigate(`/content/${contentId}`);
  };

  if (!user) {
    return (
      <Center h="100vh">
        <Text>Please log in to view your dashboard</Text>
      </Center>
    );
  }

  return (
    <Box minH="100vh" bg={bgColor} p={{ base: 3, md: 6, lg: 8 }} className="page-content">
      <VStack align="stretch" gap={{ base: 4, md: 6 }} maxW="1200px" mx="auto">
        {/* Header */}
        <Stack 
          direction={{ base: 'column', md: 'row' }} 
          justify="space-between" 
          align={{ base: 'stretch', md: 'center' }}
          gap={{ base: 4, md: 0 }}
        >
          <HStack gap={{ base: 3, md: 4 }}>
            <Button
              leftIcon={<IconWrapper icon={FiArrowLeft} size={16} />}
              variant="ghost"
              onClick={() => navigate(-1)}
              size={{ base: 'sm', md: 'sm' }}
            >
              {isMobile ? '' : 'Back'}
            </Button>
            <VStack align="start" gap={0}>
              <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="bold">
                {isMobile ? 'Dashboard' : 'Creator Dashboard'}
              </Text>
              <Text fontSize={{ base: 'xs', md: 'sm' }} color={textColor}>
                {isMobile ? 'Analytics' : 'Analytics and performance insights'}
              </Text>
            </VStack>
          </HStack>
          
          <HStack gap={{ base: 2, md: 3 }} justify={{ base: 'space-between', md: 'flex-end' }}>
            <Select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              size="sm"
              w={{ base: '140px', md: '180px' }}
            >
              <option value="all">All Content</option>
              <option value="videos">Videos</option>
              <option value="short videos">Short Videos</option>
              <option value="books">Books</option>
              <option value="series">Series</option>
              <option value="comics">Comics</option>
              <option value="movies">Movies</option>
            </Select>
            <Select
              value={timeRange}
              onChange={handleTimeRangeChange}
              size="sm"
              w={{ base: '120px', md: '150px' }}
              icon={<IconWrapper icon={FiCalendar} size={16} />}
            >
              <option value={7}>Last 7 days</option>
              <option value={28}>Last 28 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last year</option>
            </Select>
            <Button
              leftIcon={<IconWrapper icon={FiRefreshCw} size={16} />}
              onClick={handleRefresh}
              size="sm"
              variant="outline"
              isLoading={isLoading}
              display={{ base: 'none', sm: 'flex' }}
            >
              Refresh
            </Button>
          </HStack>
        </Stack>

        {/* Overview Metrics */}
        <Box>
          <Text fontSize={{ base: 'md', md: 'lg' }} fontWeight="bold" mb={{ base: 3, md: 4 }}>
            Overview
          </Text>
          <DashboardMetrics metrics={metrics} isLoading={isLoading} />
        </Box>

        {/* Last Content Performance */}
        {filteredContent.length > 0 && (
          <Box>
            <Text fontSize={{ base: 'md', md: 'lg' }} fontWeight="bold" mb={{ base: 3, md: 4 }}>
              Your Videos {categoryFilter !== 'all' && `(${categoryFilter})`}
            </Text>
            <VStack align="stretch" gap={{ base: 3, md: 4 }}>
              {filteredContent.map((content) => (
                <ContentPerformance
                  key={content.id}
                  content={content}
                  onViewDetails={handleViewDetails}
                  onEdit={(contentId) => {
                    const contentItem = recentContent.find(c => c.id === contentId);
                    if (contentItem?.category === 'movie') {
                      navigate(`/edit/movie/${contentId}`);
                    } else if (contentItem?.category === 'series') {
                      navigate(`/edit/series/${contentId}`);
                    } else if (contentItem?.category === 'short') {
                      navigate(`/edit/short/${contentId}`);
                    } else if (contentItem?.category === 'book') {
                      navigate(`/edit/book/${contentId}`);
                    } else if (contentItem?.category === 'comic') {
                      navigate(`/edit/comic/${contentId}`);
                    }
                  }}
                  onDelete={async (contentId) => {
                    if (window.confirm('Are you sure you want to delete this content? This action cannot be undone.')) {
                      try {
                        const { deleteContent } = await import('../services/contentDeletionService');
                        await deleteContent(contentId, user!.uid);
                        toast({
                          title: 'Content deleted',
                          status: 'success',
                          duration: 3000,
                          isClosable: true
                        });
                        loadDashboardData();
                      } catch (error) {
                        toast({
                          title: 'Error deleting content',
                          status: 'error',
                          duration: 3000,
                          isClosable: true
                        });
                      }
                    }
                  }}
                  onShare={async (contentId) => {
                    try {
                      const shareUrl = `${window.location.origin}/content/${contentId}`;
                      await navigator.clipboard.writeText(shareUrl);
                      toast({
                        title: 'Link copied!',
                        description: 'Content link has been copied to clipboard',
                        status: 'success',
                        duration: 2000,
                        isClosable: true
                      });
                    } catch (error) {
                      toast({
                        title: 'Error sharing',
                        status: 'error',
                        duration: 3000,
                        isClosable: true
                      });
                    }
                  }}
                />
              ))}
            </VStack>
          </Box>
        )}

        {/* Top Videos */}
        {topVideos.length > 0 && (
          <Box>
            <Text fontSize={{ base: 'md', md: 'lg' }} fontWeight="bold" mb={{ base: 3, md: 4 }}>
              Top Videos
            </Text>
            <TopVideos videos={topVideos} isLoading={isLoading} />
          </Box>
        )}

        {/* Empty State */}
        {!isLoading && recentContent.length === 0 && topVideos.length === 0 && (
          <Center py={12}>
            <VStack gap={4}>
              <Text fontSize="lg" color={textColor}>
                No content found
              </Text>
              <Text fontSize="sm" color={textColor} textAlign="center">
                Upload your first video to start tracking analytics
              </Text>
              <Button
                colorScheme="purple"
                onClick={() => navigate('/upload')}
              >
                Upload Content
              </Button>
            </VStack>
          </Center>
        )}
      </VStack>
    </Box>
  );
};

export default CreatorDashboard;